/**
 * Lifecycle campaign Builder — marketing self-serve campaign creation.
 *
 * Left: the builder agent chat (paste a campaign brief; the agent maps it
 * onto the validated definition envelope, lints the linked AC automation,
 * estimates the cohort, and publishes DARK). Right: definition drafts with
 * validation/preflight state and a publish action. Launching stays on each
 * campaign's page toggle — nothing here can email patients.
 */
import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Plus, RefreshCw, Send } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { cn } from '@/lib/cn';
import {
  type BuilderDefinitionRow,
  useBuilderDefinitions,
  useBuilderPreflight,
  useBuilderPublish,
  useBuilderThread,
  useBuilderThreads,
  useCreateBuilderThread,
  useSendBuilderMessage,
} from '@/lib/api/lifecycleAdminHooks';

const STATUS_STYLES: Record<string, string> = {
  draft: 'bg-sunken text-ink-secondary',
  published: 'bg-[var(--accent)] text-white',
  archived: 'bg-sunken text-ink-muted line-through',
};

export function LifecycleBuilderScreen() {
  const [params, setParams] = useSearchParams();
  const threadId = params.get('thread');
  const threadsQ = useBuilderThreads();
  const createThread = useCreateBuilderThread();

  const selectThread = (id: string | null) => {
    const next = new URLSearchParams(params);
    if (id) next.set('thread', id);
    else next.delete('thread');
    setParams(next, { replace: true });
  };

  const onNewThread = async () => {
    const res = await createThread.mutateAsync();
    selectThread(res.thread_id);
  };

  return (
    <div className="flex flex-col gap-20 p-20">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="type-title-md">Campaign builder</h1>
          <p className="type-body-sm text-ink-muted">
            Paste a campaign brief and the agent builds it: eligibility, AC
            wiring checks, cohort estimate, merge fields. Everything publishes
            dark — launching is still each campaign&apos;s toggle on{' '}
            <Link to="/admin/lifecycle-emails" className="underline">
              Lifecycle emails
            </Link>
            .
          </p>
        </div>
        <button
          type="button"
          className="inline-flex items-center gap-6 type-label-sm text-ink-secondary hover:text-ink"
          onClick={onNewThread}
        >
          <Plus size={14} /> New thread
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] gap-20 items-start">
        <div className="border border-line-subtle bg-page">
          <div className="flex items-center gap-8 border-b border-line-subtle bg-sunken px-12 py-8 overflow-x-auto">
            {(threadsQ.data ?? []).slice(0, 8).map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => selectThread(t.id)}
                className={cn(
                  'type-label-xs px-10 py-6 border whitespace-nowrap max-w-[22rem] truncate',
                  t.id === threadId
                    ? 'border-ink text-ink'
                    : 'border-line-subtle text-ink-muted hover:text-ink',
                )}
              >
                {t.title || 'Untitled'}
              </button>
            ))}
            {!threadsQ.data?.length && (
              <span className="type-body-xs text-ink-muted">
                No threads yet — start one and paste a campaign brief.
              </span>
            )}
          </div>
          <ChatPanel threadId={threadId} />
        </div>

        <DefinitionsPanel />
      </div>
    </div>
  );
}

function ChatPanel({ threadId }: { threadId: string | null }) {
  const threadQ = useBuilderThread(threadId);
  const send = useSendBuilderMessage();
  const [draft, setDraft] = useState('');
  const bottomRef = useRef<HTMLDivElement | null>(null);

  const messages = threadQ.data?.messages ?? [];
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length, send.isPending]);

  const onSend = () => {
    const content = draft.trim();
    if (!content || !threadId || send.isPending) return;
    setDraft('');
    send.mutate({ threadId, content });
  };

  if (!threadId) {
    return (
      <div className="p-30 type-body-sm text-ink-muted">
        Create a thread, then paste the campaign brief (audience, timing, CTA
        destinations, the ActiveCampaign automation link). The agent knows the
        product catalog, the eligibility knobs, and the AC gotchas.
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[60rem]">
      <div className="flex-1 overflow-y-auto p-15 flex flex-col gap-12">
        {messages.map((m) => (
          <div
            key={m.id}
            className={cn(
              'max-w-[90%] px-12 py-10 type-body-sm leading-140',
              m.role === 'user'
                ? 'self-end bg-sunken border border-line-subtle'
                : 'self-start',
            )}
          >
            <div className="prose-sm [&_ul]:list-disc [&_ul]:pl-16 [&_code]:bg-sunken [&_code]:px-4">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {m.content_text}
              </ReactMarkdown>
            </div>
          </div>
        ))}
        {send.isPending && (
          <div className="self-start type-body-sm text-ink-muted animate-pulse">
            Working — checking AC / cohorts can take ~a minute…
          </div>
        )}
        <div ref={bottomRef} />
      </div>
      <div className="border-t border-line-subtle p-10 flex gap-8">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) onSend();
          }}
          rows={3}
          placeholder="Paste a campaign brief, or ask about existing campaigns… (⌘↵ to send)"
          className="flex-1 resize-none bg-page border border-line-subtle px-10 py-8 type-body-sm focus:outline-none focus:border-ink"
        />
        <button
          type="button"
          onClick={onSend}
          disabled={send.isPending || !draft.trim()}
          className="self-end inline-flex items-center gap-6 border border-ink px-12 py-8 type-label-sm disabled:opacity-40"
        >
          Send <Send size={13} />
        </button>
      </div>
    </div>
  );
}

function DefinitionsPanel() {
  const { data, isLoading, refetch, isFetching } = useBuilderDefinitions();
  return (
    <div className="border border-line-subtle bg-page">
      <div className="flex items-center justify-between border-b border-line-subtle bg-sunken px-12 py-8">
        <span className="type-label-sm text-ink-secondary">DEFINITIONS</span>
        <button
          type="button"
          onClick={() => refetch()}
          className="text-ink-muted hover:text-ink"
          aria-label="Refresh definitions"
        >
          <RefreshCw size={13} className={cn(isFetching && 'animate-spin')} />
        </button>
      </div>
      {isLoading ? (
        <div className="p-15 type-body-sm text-ink-muted">Loading…</div>
      ) : !data?.length ? (
        <div className="p-15 type-body-sm text-ink-muted">
          No agent-built campaigns yet. Definitions the agent drafts and
          publishes appear here; published ones also get a card (dark) on the
          Lifecycle emails page.
        </div>
      ) : (
        <div className="flex flex-col divide-y divide-line-subtle">
          {data.map((d) => (
            <DefinitionRow key={d.campaign_key} row={d} />
          ))}
        </div>
      )}
    </div>
  );
}

function DefinitionRow({ row }: { row: BuilderDefinitionRow }) {
  const [open, setOpen] = useState(false);
  const preflight = useBuilderPreflight();
  const publish = useBuilderPublish();
  const [publishError, setPublishError] = useState<string | null>(null);

  const failing = (row.preflight?.checks ?? []).filter((c) => c.status === 'fail');
  const warnings = (row.preflight?.checks ?? []).filter((c) => c.status === 'warn');

  const onPublish = async () => {
    setPublishError(null);
    try {
      await publish.mutateAsync(row.campaign_key);
    } catch (e) {
      setPublishError(e instanceof Error ? e.message : 'publish failed');
    }
  };

  return (
    <div className="px-12 py-10 flex flex-col gap-8">
      <div className="flex items-center justify-between gap-10">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="type-body-sm text-ink text-left truncate hover:underline"
        >
          {row.campaign_key}
        </button>
        <div className="flex items-center gap-8 shrink-0">
          <span
            className={cn(
              'type-label-xs px-8 py-3',
              STATUS_STYLES[row.status] ?? 'bg-sunken',
            )}
          >
            {row.status.toUpperCase()}
          </span>
          {row.validation && (
            <span
              className={cn(
                'type-label-xs',
                row.validation.ok ? 'text-ink-muted' : 'text-red-600',
              )}
            >
              {row.validation.ok ? 'valid' : 'invalid'}
            </span>
          )}
        </div>
      </div>

      {open && (
        <div className="flex flex-col gap-8">
          {!row.validation?.ok &&
            (row.validation?.errors ?? []).map((e, i) => (
              <div key={i} className="type-body-xs text-red-600">
                {e.field}: {e.message}
              </div>
            ))}
          {failing.map((c, i) => (
            <div key={i} className="type-body-xs text-red-600">
              ✗ {c.detail}
            </div>
          ))}
          {warnings.map((c, i) => (
            <div key={i} className="type-body-xs text-amber-600">
              ⚠ {c.detail}
            </div>
          ))}
          {row.preflight?.cohort && (
            <div className="type-body-xs text-ink-secondary">
              Cohort sample: {JSON.stringify(row.preflight.cohort)}
            </div>
          )}
          {publishError && (
            <div className="type-body-xs text-red-600">{publishError}</div>
          )}
          <div className="flex gap-8">
            <button
              type="button"
              onClick={() => preflight.mutate(row.campaign_key)}
              disabled={preflight.isPending}
              className="type-label-xs border border-line-subtle px-10 py-6 hover:border-ink disabled:opacity-40"
            >
              {preflight.isPending ? 'Checking…' : 'Run preflight'}
            </button>
            {row.status === 'draft' && (
              <button
                type="button"
                onClick={onPublish}
                disabled={publish.isPending || !row.validation?.ok}
                className="type-label-xs border border-ink px-10 py-6 disabled:opacity-40"
              >
                {publish.isPending ? 'Publishing…' : 'Publish (dark)'}
              </button>
            )}
            {row.status === 'published' && (
              <Link
                to={`/admin/lifecycle-emails/${row.campaign_key}`}
                className="type-label-xs border border-line-subtle px-10 py-6 hover:border-ink"
              >
                Campaign page →
              </Link>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
